import type { createDebug } from '@shared/debug';
import { imagePathResolver, ROUTES } from '@shared/utils';
import type { useTranslations } from 'next-intl';
import type { GameDetailsFromApi } from '../../../../casino/types/types';
import type { ContentType } from '../../../../types';
import type { FreeSpinsOffer, FreeSpinsStatus } from '../../../types/types';

type MapFreeSpinsArgs = {
  item: FreeSpinsOffer;
  t: ReturnType<typeof useTranslations>;
  games: GameDetailsFromApi[];
  baseImageUrl: string;
  debug: ReturnType<typeof createDebug>;
};

function mapFreeSpinsStatus(status: FreeSpinsStatus, debug: ReturnType<typeof createDebug>) {
  if (status === 'ACTIVE') return 'active';
  if (status === 'EXPIRED') return 'expired';
  if (status === 'USED') return 'completed';
  if (status === 'PENDING') return 'pending';
  debug.warn(`Unknown free spins status: ${status satisfies never}`);
}

function mapFreeSpins({ item, t, games, baseImageUrl, debug }: MapFreeSpinsArgs) {
  const { id, gameName, endDate, status, freeSpins, initialFreeSpins, wins } = item;
  const game = games?.find(({ name }) => name === gameName);
  const mappedStatus = mapFreeSpinsStatus(status, debug);

  // initialFreeSpins = total awarded spins
  // freeSpins = current remaining spins
  const initial = initialFreeSpins;
  const currentRemaining = freeSpins;
  // Ensure used spins is never negative (safeguard against invalid data)
  const used = Math.max(0, initial - currentRemaining);

  const data: Partial<ContentType> = {
    id,
    title: game?.displayName,
    gameLogo: imagePathResolver(game?.logo || '', baseImageUrl),
    description: ['USED', 'EXPIRED'].includes(status) ? undefined : t('free_spins_description', { freeSpins: initial }),
    cta: t('cta.from_status.free_spins', { status: status.toLowerCase() }),
    ctaHref: mappedStatus === 'active' ? ROUTES.CASINO.GAME(gameName) : undefined,
    expirationDate: endDate,
    status: mappedStatus,
    image: { src: '/images/free-spins-bg.png', alt: 'Free spins background' },
    progress: {
      balance: currentRemaining,
      progress: { current: used, max: initial, labelFormat: 'currentOfMax' },
    },
    bonusProgressText: t('free_spins_use_progress'),
    wins,
  };

  return data;
}

export { mapFreeSpins };
